import { useState, useEffect } from 'react';
import { FunnelData, FunnelAPIResponse } from '../types';
import useExternalApiService from '../../../helpers/ExternalApiService';
import { store } from '../../../stores/Store';

interface UseFunnelDataParams {
  startDate?: string;  // Format: YYYY-MM-DD
  endDate?: string;    // Format: YYYY-MM-DD
}

interface UseFunnelDataReturn {
  data: FunnelData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch and manage sales funnel data
 * Implements 5-minute cache TTL to reduce API calls
 * Now supports date range filtering
 */
export const useFunnelData = ({ startDate, endDate }: UseFunnelDataParams = {}): UseFunnelDataReturn => {
  const [data, setData] = useState<FunnelData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);
  const [lastDateRange, setLastDateRange] = useState<string>('');

  const { getData } = useExternalApiService(process.env.REACT_APP_EAI_URL || '');

  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  const fetchFunnelData = async () => {
    const now = Date.now();
    const currentDateRange = `${startDate || ''}-${endDate || ''}`;

    // Check cache - bypass if date range changed
    if (data && now - lastFetch < CACHE_TTL && currentDateRange === lastDateRange) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query parameters for date filtering and user email
      const queryParams = new URLSearchParams();
      if (store.impersonatedUser) queryParams.append('fmc_email', store.impersonatedUser);
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);

      const endpoint = `/api/winback_funnel_stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await getData(endpoint);

      if (response && response.data) {
        setData(response.data);
        setLastFetch(now);
        setLastDateRange(currentDateRange);
      } else {
        console.error('❌ Invalid response format:', response);
        throw new Error('Invalid response format');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch funnel data';
      setError(errorMessage);
      console.error('Error fetching funnel data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunnelData();
    // Refetch when date range changes
  }, [startDate, endDate]);

  return {
    data,
    loading,
    error,
    refetch: fetchFunnelData,
  };
};
